import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { store, persistor } from './store';
import App from './App';
import './styles/index.css';
import './styles/cssOrder.js'; // pins CSS-module order; see that file
import { installChunkReloadGuard } from './utils/chunkReloadGuard';

// A tab left open across a deploy reloads once instead of going blank on a missing chunk.
installChunkReloadGuard();

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
    },
  },
});

// Loading component while redux-persist rehydrates
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid rgba(255,255,255,0.3)',
        borderTopColor: 'white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <span style={{ color: 'white', fontSize: '18px', fontWeight: '500' }}>
        Loading...
      </span>
    </div>
  </div>
);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={<LoadingScreen />} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: 'var(--color-surface, #ffffff)',
                  color: 'var(--color-text, #111827)',
                  border: '1px solid var(--color-border, #e2e8f0)',
                  borderRadius: 'var(--radius-lg, 10px)',
                  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(15, 23, 42, 0.10))',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  maxWidth: '420px',
                },
                success: {
                  iconTheme: {
                    primary: 'var(--color-success-text, #16a34a)',
                    secondary: '#ffffff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: 'var(--color-danger, #dc2626)',
                    secondary: '#ffffff',
                  },
                },
              }}
            />
          </BrowserRouter>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
